import classNames from "classnames";
import Link from "next/link";
import Router from "next/router";
import React, { useState } from "react";
import { ProductItemInterface, StoreInterface } from "../../assets/interfaces";
import { capitalizeFirstLetter, toAppUrl, toCurrency } from "../../assets/js/utils/functions";
import {
  checkItemAvailability,
  getDiscountPercentage,
  getFormattedDate,
  getProductOptionsLength,
} from "../../assets/js/utils/utils";
import CartContext from "../../contexts/cart-context";
import useCopyClipboard from "../hooks/useCopyClipboard";
import { useModals } from "../hooks/useModals";
import { StoreFrontCart } from "../storefronts-v1";
import Badge from "../ui/badge";
import { AppBtn } from "../ui/buttons";
import ImageWithPlaceholder from "../ui/image-with-placeholder";
import ImageCarouselModal from "./image-carousel-modal";
import { ProductImages } from "./modals/product-details";
import { DiscountBadge } from "../storefronts-v1/ui/bages";

interface IProps {
  item: Omit<ProductItemInterface, "store"> & { store: StoreInterface };
}

const ProductPageDetails: React.FC<IProps> = ({ item }) => {
  const {
    addOrRemoveItem,
    isItemInCart,
    cart,
    toggleModal: toggleModalFromCart,
    cartId,
    formatAsCurrency,
  } = CartContext.useContainer();
  const discountExists = item.discount_price !== undefined && item.discount_price > 0;
  const { modals, toggleModal } = useModals(["buy_item", "image_carousel"]);
  const [currentImage, setCurrentImage] = useState(0);
  const [copied, copy] = useCopyClipboard(toAppUrl(`products/${item?.slug}`), {
    successDuration: 1000,
  });

  const store = item?.store;
  const itemWithoutStore = { ...item, store: item?.store?.id };
  const itemIsAvailable = checkItemAvailability({ ...item, store: item?.store?.id });

  const showFullImage = (index: number) => {
    setCurrentImage(index);
    toggleModal("image_carousel");
  };

  const handleItemPageSelection = () => {
    if (cart.length < 1 || item?.variants?.options.length > 0) {
      addOrRemoveItem(item as any);
    } else {
      toggleModalFromCart("cart");
    }
  };

  const createStoreCartAndNavigate = () => {
    let carts = JSON.parse(localStorage.getItem("catlog-carts") || "{}");

    carts[item?.store.id] = cartId;
    localStorage.setItem("catlog-carts", JSON.stringify(carts));
    Router.push(`/${item?.store.slug}`);
  };

  const shareProduct = async () => {
    const shareData = {
      title: item?.name,
      text: "Check out this item on Catlog!",
      url: toAppUrl(`/products/${item?.slug}`),
    };

    try {
      await navigator.share(shareData);
    } catch (err) {
      // console.log("Couldnt share product", err);
    }
  };

  return (
    <>
      <div className="py-6.25 sm:py-8 px-5 sm:px-7.5 flex-1 overflow-y-auto h-full">
        <h2 className="text-black font-bold text-xl sm:text-2lg lg:text-2xl pb-2 mb-3.75 border-b border-grey-border border-opacity-30">
          {capitalizeFirstLetter(item?.name)}
        </h2>
        <div className="mb-4 space-y-3.5">
          {(item?.quantity || item.is_always_available) && item?.available && (
            <div className="flex">
              <Badge
                text={
                  item.is_always_available
                    ? "In Stock"
                    : item?.quantity > 0
                    ? `${item.quantity} in stock`
                    : "Out of stock"
                }
                className={item?.quantity > 0 || item.is_always_available ? "!text-success" : "!text-accent-red-500"}
              />
            </div>
          )}
          <div className="flex items-center justify-between mb-2.5">
            <div
              className={classNames("text-1xs sm:text-sm text-grey-subtext", {
                "flex items-center": !discountExists,
              })}
            >
              {discountExists && (
                <div
                  className={`text-black font-semibold text-1sm sm:text-base ${
                    ""
                    // item.available ? "text-opacity-100" : "text-opacity-70"
                  }`}
                >
                  {formatAsCurrency(item.discount_price)}
                </div>
              )}
              <div
                className={classNames({
                  "line-through text-xs sm:text-1xs": discountExists,
                  "text-black font-semibold text-1sm sm:text-base -mb-2": !discountExists,
                })}
              >
                {formatAsCurrency(item.price)}
              </div>
            </div>
            {discountExists && itemIsAvailable && (
              // <div className="bg-accent-red-300 rounded-lg px-2 py-1.25 sm:px-2.5 sm:py-1.75 text-[9px] sm:text-xxs text-white font-semibold">
              //   {getDiscountPercentage(itemWithoutStore)}% OFF
              // </div>
              <DiscountBadge percentage={getDiscountPercentage(itemWithoutStore)} />
            )}

            {!itemIsAvailable && <Badge text="Unavailable" color="red" />}
          </div>
          {item?.variants?.options.length > 0 && (
            <div className="w-full bg-grey-fields-100 rounded-5 flex items-center text-dark text-1xs py-2.5 px-4 justify-between">
              <div className="inline-flex items-center">
                {/* prettier-ignore */}
                <svg width="14" viewBox="0 0 12 12" fill="none">
                    <path d="M5 1.5H1.5V5H5V1.5Z" stroke="#737576" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10.5001 1.5H7.00009V5H10.5001V1.5Z" stroke="#737576" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10.5001 7H7.00009V10.5H10.5001V7Z" stroke="#737576" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M5 7H1.5V10.5H5V7Z" stroke="#737576" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                <span className="inline-block ml-2.5 font-medium">
                  {getProductOptionsLength(item?.variants)} options available
                </span>
              </div>

              <button
                className="no-outline text-primary-500 flex items-center text-1xs font-semibold"
                onClick={() => addOrRemoveItem(itemWithoutStore)}
              >
                <span className="">See Options</span>
                {/* prettier-ignore */}
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none" className="ml-0.75">
                  <path d="M1 7L7 1" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.2}/>
                  <path d="M1 1H7V7" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.2}/>
                </svg>
              </button>
            </div>
          )}
        </div>
        <dl className="flex flex-col space-y-3.5 pt-3.75 border-t border-grey-divider">
          <div className="flex w-full justify-between items-center">
            <dt className="text-1xs sm:text-sm text-dark">Product Link:</dt>
            <dd className="inline-flex items-center -mt-1" onClick={() => copy()}>
              <span className="text-1xs sm:text-sm font-medium text-primary-500 max-w-[150px] lg:max-w-[220px] overflow-hidden overflow-ellipsis whitespace-nowrap cursor-pointer">
                {copied ? "Copied!" : toAppUrl(`products/${item?.slug}`, false)}
              </span>
              <button
                type="button"
                className="h-5 w-5 rounded-5 bg-grey-fields-100 flex items-center justify-center ml-1 text-primary-500"
              >
                {/* prettier-ignore */}
                <svg width="12" viewBox="0 0 10 10" fill="none">
                  <path d="M8.33333 3.75H4.58333C4.1231 3.75 3.75 4.1231 3.75 4.58333V8.33333C3.75 8.79357 4.1231 9.16667 4.58333 9.16667H8.33333C8.79357 9.16667 9.16667 8.79357 9.16667 8.33333V4.58333C9.16667 4.1231 8.79357 3.75 8.33333 3.75Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M2.08337 6.25016H1.66671C1.44569 6.25016 1.23373 6.16237 1.07745 6.00608C0.921171 5.8498 0.833374 5.63784 0.833374 5.41683V1.66683C0.833374 1.44582 0.921171 1.23385 1.07745 1.07757C1.23373 0.921293 1.44569 0.833496 1.66671 0.833496H5.41671C5.63772 0.833496 5.84968 0.921293 6.00596 1.07757C6.16224 1.23385 6.25004 1.44582 6.25004 1.66683V2.0835" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </dd>
          </div>
          {item?.category && (
            <div className="flex w-full justify-between items-center">
              <dt className="text-1xs sm:text-sm text-dark">Product Category:</dt>
              <dd className={`no-outline inline-flex items-center rounded-full flex-shrink-0`}>
                {item.category?.emoji && (
                  <span className="shadow-sm rounded-full w-6 h-6 p-0.5 text-1xs sm:text-sm flex items-center justify-center bg-white mr-1.25">
                    {item.category?.emoji}
                  </span>
                )}
                <span className="inline-block text-black-secondary font-medium text-1xs sm:text-sm">
                  {item.category?.name}
                </span>
              </dd>
            </div>
          )}
          {item?.expiry_date && !!new Date(item?.expiry_date) && (
            <div className="flex w-full justify-between items-center">
              <dt className="text-1xs sm:text-sm text-dark">Expiry Date:</dt>
              <dd className={`no-outline inline-flex items-center rounded-full flex-shrink-0`}>
                <span className="inline-block text-black-secondary font-medium text-1xs sm:text-sm">
                  {getFormattedDate(new Date(item?.expiry_date))}
                </span>
              </dd>
            </div>
          )}
          <div className="flex w-full flex-col space-y-1.5 pt-2.5 !mt-3.5 border-t border-grey-border border-opacity-50">
            <h4 className="text-1sm sm:text-base text-black font-display font-bold">Description:</h4>
            <dd className="text-black-secondary text-1xs sm:text-sm whitespace-pre-line">{item?.description}</dd>
          </div>
        </dl>
        <div className="flex items-center gap-5 mt-3.75">
          {item.category && (
            <a
              target="_blank"
              rel="noreferrer"
              href={`/${store?.slug}?category=${item?.category?.id}`}
              className="flex text-primary-500 items-center font-semibold gap-1.25"
            >
              <span className=" text-1xs sm:text-sm">Find More {item?.category?.name ?? ""}</span>
              {/* prettier-ignore */}
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" >
                <path d="M3.53589 11.0359L10.607 3.96482" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10.6062 11.0359L10.6062 3.96482L3.53513 3.96482" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </a>
          )}
          <button className="flex text-primary-500 items-center font-semibold gap-1.25" onClick={shareProduct}>
            <span className=" text-1xs sm:text-sm">Share this Product</span>
            {/* prettier-ignore */}
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" >
              <path d="M3.53589 11.0359L10.607 3.96482" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10.6062 11.0359L10.6062 3.96482L3.53513 3.96482" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <div className="mt-7.5">
          {/* <h2 className="text-lg sm:text-xl lg:text-lg font-bold text-black-secondary">Sold By</h2> */}
          <h4 className="text-1sm sm:text-base text-black font-display font-bold">Sold By</h4>
          <div className="mt-2.5 flex items-start gap-2.5">
            <ImageWithPlaceholder className="h-10 w-10" image={store.logo} text={store.name} />
            <div>
              <h6
                className="font-display text-1sm sm:text-base font-bold leading-none -mb-1
              "
              >
                {store.name}
              </h6>
              <span className="text-1xs text-black-muted inline-block leading-tight mt-1.5">{store.description}</span>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`flex items-center py-2.5 sm:py-3 px-5 sm:px-6.25 border-t border-grey-border border-opacity-30 fixed md:absolute bottom-0 w-full bg-white`}
      >
        <div className="mt-2.5 w-full">
          <div className="flex w-full">
            <AppBtn className="flex-1 mr-3" color="neutral" onClick={createStoreCartAndNavigate} size="lg">
              Browse more items
            </AppBtn>
            <AppBtn isBlock onClick={handleItemPageSelection} className="flex-1" disabled={!itemIsAvailable} size="lg">
              Buy Item
            </AppBtn>
          </div>
        </div>
      </div>

      <StoreFrontCart phoneNumber={item.store.phone} store={item?.store} productPage />
      {item?.images && (
        <ImageCarouselModal
          show={modals.image_carousel}
          toggle={() => toggleModal("image_carousel")}
          images={item?.images}
          current={currentImage}
        />
      )}
    </>
  );
};

export default ProductPageDetails;
